import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';
import { FileUpload } from '@/components/FileUpload';
import {
  UnsavedChangesGuard,
  type HeldNavigation,
} from '@/components/UnsavedChangesGuard/UnsavedChangesGuard';

import { useCourse } from '@/features/courses/useCourse';

import { ChapterContextPicker, type ChapterOption } from './ChapterContextPicker';
import { DiscardCoachChangesDialog } from './DiscardCoachChangesDialog';
import { CoachEditBar } from './KaiCoachEditControls';
import { KaiCoachStudentView } from './KaiCoachStudentView';
import {
  newActivity,
  newFocusArea,
  newNote,
  newSession,
  serializeCoachSection,
  toEditableCoach,
  type EditableActivity,
  type EditableCoach,
  type EditableFocusArea,
  type EditableGoal,
  type EditableNote,
  type EditableScoringMethod,
  type EditableSession,
} from './kaiCoachEditorModel';
import type { RawKaiCoachSection } from './kaiCoachTypes';
import { useSaveKaiCoach, type SaveKaiCoachContext } from './useSaveKaiCoach';
import styles from './KaiCoachEditor.module.css';

interface KaiCoachEditorProps {
  context: SaveKaiCoachContext;
  // Move focus to the editor's heading once it opens: set when the instructor
  // opened it from the page's "Edit coach" button, not from a link.
  focusOnOpen?: boolean;
  // Leaves edit mode. Called by Done, and by Cancel once the instructor has
  // chosen to discard.
  onExit?: () => void;
  section: RawKaiCoachSection;
}

function millisToLocalInput(millis: string): string {
  const n = Number(millis);
  if (!Number.isFinite(n) || !millis) {
    return '';
  }
  // datetime-local wants "YYYY-MM-DDTHH:mm" in local time.
  const date = new Date(n);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localInputToMillis(value: string): string {
  if (!value) {
    return '';
  }
  const millis = new Date(value).getTime();
  return Number.isFinite(millis) ? String(millis) : '';
}

// A session is reachable by students once its open date has passed — and a
// session with NO open date is open immediately (matching the server's
// coachSessionOpenAt, which treats an unset/unparseable date as open).
function isSessionLive(session: EditableSession): boolean {
  if (!session.openAtMillis) {
    return true;
  }
  const openAt = Number(session.openAtMillis);
  return !Number.isFinite(openAt) || openAt <= Date.now();
}

// A due date that is not after the open date leaves students no window at all.
// Only both-set, both-parseable pairs can conflict; anything else is fine.
function dueNotAfterOpen(openAtMillis: string, dueAtMillis: string): boolean {
  if (!openAtMillis || !dueAtMillis) {
    return false;
  }
  const openAt = Number(openAtMillis);
  const dueAt = Number(dueAtMillis);
  return Number.isFinite(openAt) && Number.isFinite(dueAt) && dueAt <= openAt;
}

const summaryDateFormat = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  month: 'short',
});

function summaryDate(millis: string): string | null {
  const n = Number(millis);
  return millis && Number.isFinite(n) ? summaryDateFormat.format(new Date(n)) : null;
}

const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

function pointsLabel(points: number): string {
  return `${numberFormat.format(points)} ${points === 1 ? 'point' : 'points'}`;
}

// A long authored text shown back in help text, cut to a readable length.
function excerpt(text: string, max = 140): string {
  const flat = text.trim().replace(/\s+/g, ' ');
  return flat.length > max ? `${flat.slice(0, max - 1).trimEnd()}…` : flat;
}

// --- credit resolution ----------------------------------------------------------
//
// Under goal-reached credit each session (or activity, when a session is split)
// uses the coach's default for any credit value it leaves blank, one value at a
// time. These mirror the backend's coachUnitPoints / coachResolvedGoal so the
// editor's notes and summaries say what will actually happen.

interface CreditDefaults {
  goal: EditableGoal;
  points: string;
}

// The unit's own points when it has any (an explicit 0 included), else the
// default's; 0 when neither parses.
function resolvedPoints(own: string, defaults: CreditDefaults | null): number {
  const value = own.trim() !== '' ? own : defaults?.points ?? '';
  const n = Number(value.trim());
  return value.trim() !== '' && Number.isFinite(n) ? n : 0;
}

function resolvedHasGoal(own: EditableGoal, defaults: CreditDefaults | null): boolean {
  return own.goalCriteria.trim() !== '' || (defaults?.goal.goalCriteria.trim() ?? '') !== '';
}

// A unit with points but nothing for Kai to judge can never be awarded.
function unitNeedsGoal(
  unit: EditableGoal & { points: string },
  defaults: CreditDefaults | null,
): boolean {
  return defaults !== null && resolvedPoints(unit.points, defaults) > 0 && !resolvedHasGoal(unit, defaults);
}

function fileUid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `file-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// --- building blocks ---------------------------------------------------------------

// A named group of related fields: a fieldset whose legend is its visible title
// and whose help text is its description. "credit" tints the group so points,
// goal and the student line read as one thing.
function FieldGroup({
  children,
  help,
  legend,
  tone,
}: {
  children: ReactNode;
  help?: ReactNode;
  legend: string;
  tone?: 'credit';
}) {
  const helpId = useId();
  return (
    <fieldset
      aria-describedby={help ? helpId : undefined}
      className={tone === 'credit' ? styles.creditGroup : styles.group}
    >
      <legend className={styles.groupLegend}>{legend}</legend>
      <div className={styles.groupBody}>
        {help ? (
          <p className={styles.help} id={helpId}>
            {help}
          </p>
        ) : null}
        {children}
      </div>
    </fieldset>
  );
}

// A labelled control with visible help text tied to it by aria-describedby.
function Field({
  children,
  help,
  label,
}: {
  children: (describedBy: string | undefined) => ReactNode;
  help?: ReactNode;
  label: ReactNode;
}) {
  const helpId = useId();
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>
        {label}
        {children(help ? helpId : undefined)}
      </label>
      {help ? (
        <p className={styles.fieldHelp} id={helpId}>
          {help}
        </p>
      ) : null}
    </div>
  );
}

// The "Due on" field a session and each of its activities carry. Sessions and
// activities differ only in the help text and in what the date overrides.
function DueDateField({
  dueAtMillis,
  help,
  onChange,
  openAtMillis,
}: {
  dueAtMillis: string;
  help: string;
  onChange: (dueAtMillis: string) => void;
  // The open date the due date is checked against (the session's, for an
  // activity too: activities open with their session).
  openAtMillis: string;
}) {
  return (
    <div className={styles.field}>
      <Field help={help} label="Due on">
        {(describedBy) => (
          <input
            aria-describedby={describedBy}
            className={styles.input}
            onChange={(event) => onChange(localInputToMillis(event.target.value))}
            type="datetime-local"
            value={millisToLocalInput(dueAtMillis)}
          />
        )}
      </Field>
      {dueNotAfterOpen(openAtMillis, dueAtMillis) ? (
        <p aria-live="polite" className={styles.liveWarning} role="status">
          This due date is not after the open date, so students would have no time
          to send a message. Check the two dates.
        </p>
      ) : null}
    </div>
  );
}

function NoteEditor({
  noun,
  note,
  onChange,
  onRemove,
  showVisibility,
  storagePathPrefix,
}: {
  // "Instruction" for standing instructions, "Coaching note" otherwise.
  noun: string;
  note: EditableNote;
  onChange: (next: EditableNote) => void;
  onRemove: () => void;
  // Standing instructions keep a Draft/Live/Schedule control; session and
  // activity coaching notes do not — they are sent to Kai once their session
  // opens to students.
  showVisibility: boolean;
  storagePathPrefix: string;
}) {
  return (
    <div className={styles.note}>
      <div className={styles.itemHeader}>
        <input
          aria-label={`${noun} label`}
          className={styles.input}
          onChange={(event) => onChange({ ...note, label: event.target.value })}
          placeholder="Label (e.g. Week 3 — using evidence)"
          value={note.label}
        />
        <button
          aria-label={`Remove ${note.label.trim() || noun.toLowerCase()}`}
          className={styles.textButton}
          onClick={onRemove}
          type="button"
        >
          Remove
        </button>
      </div>

      <label className={styles.fieldLabel}>
        What Kai should do (private to the AI)
        <textarea
          className={styles.textarea}
          onChange={(event) => onChange({ ...note, content: event.target.value })}
          placeholder="Coach the student Socratically. Never write their work for them…"
          rows={3}
          value={note.content}
        />
      </label>

      {showVisibility ? (
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel}>
            Visible to Kai
            <select
              className={styles.select}
              onChange={(event) =>
                onChange({
                  ...note,
                  visibility: event.target.value as EditableNote['visibility'],
                })
              }
              value={note.visibility}
            >
              <option value="DRAFT">Draft — never sent to Kai</option>
              <option value="LIVE">Live — sent now</option>
              <option value="SCHEDULED">Scheduled — sent from a date</option>
            </select>
          </label>
          {note.visibility === 'SCHEDULED' ? (
            <label className={styles.fieldLabel}>
              Goes live
              <input
                className={styles.input}
                onChange={(event) =>
                  onChange({ ...note, liveAtMillis: localInputToMillis(event.target.value) })
                }
                type="datetime-local"
                value={millisToLocalInput(note.liveAtMillis)}
              />
            </label>
          ) : null}
        </div>
      ) : null}

      {note.files.length > 0 ? (
        <ul className={styles.fileList}>
          {note.files.map((file) => (
            <li className={styles.file} key={file.uid}>
              <span className={styles.fileName}>{file.filename || 'Attached file'}</span>
              <input
                aria-label={`Description for ${file.filename || 'file'} (required)`}
                className={styles.input}
                onChange={(event) =>
                  onChange({
                    ...note,
                    files: note.files.map((f) =>
                      f.uid === file.uid ? { ...f, description: event.target.value } : f,
                    ),
                  })
                }
                placeholder="Required: tell Kai what this file is"
                value={file.description}
              />
              <button
                className={styles.textButton}
                onClick={() =>
                  onChange({ ...note, files: note.files.filter((f) => f.uid !== file.uid) })
                }
                type="button"
              >
                Remove file
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <FileUpload
        accept=".pdf,.txt,.md,.docx"
        buttonLabel="Attach reference file"
        onUploadComplete={(result) =>
          onChange({
            ...note,
            files: [
              ...note.files,
              {
                description: '',
                downloadUrl: result.downloadUrl,
                filename: result.fileName,
                storagePath: result.storagePath,
                uid: fileUid(),
              },
            ],
          })
        }
        storagePath={storagePathPrefix}
      />
    </div>
  );
}

function NoteList({
  addLabel,
  emptyText,
  noun,
  notes,
  onChange,
  showVisibility,
  storagePathPrefix,
}: {
  addLabel: string;
  emptyText: string;
  noun: string;
  notes: EditableNote[];
  onChange: (next: EditableNote[]) => void;
  showVisibility: boolean;
  storagePathPrefix: string;
}) {
  return (
    <>
      {notes.length === 0 ? <p className={styles.empty}>{emptyText}</p> : null}
      {notes.map((note) => (
        <NoteEditor
          key={note.uid}
          note={note}
          noun={noun}
          onChange={(next) => onChange(notes.map((n) => (n.uid === note.uid ? next : n)))}
          onRemove={() => onChange(notes.filter((n) => n.uid !== note.uid))}
          showVisibility={showVisibility}
          storagePathPrefix={storagePathPrefix}
        />
      ))}
      <div>
        <Button onClick={() => onChange([...notes, newNote()])} variant="secondary">
          {addLabel}
        </Button>
      </div>
    </>
  );
}

// Coaching notes for a session or an activity: private to Kai, sent once the
// session opens to students.
function CoachingNotesGroup({
  notes,
  onChange,
  storagePathPrefix,
}: {
  notes: EditableNote[];
  onChange: (next: EditableNote[]) => void;
  storagePathPrefix: string;
}) {
  return (
    <FieldGroup
      help="How Kai should coach this assignment. Private to Kai; sent once the session opens."
      legend="Coaching notes"
    >
      <NoteList
        addLabel="Add coaching note"
        emptyText="No coaching notes yet."
        noun="Coaching note"
        notes={notes}
        onChange={onChange}
        showVisibility={false}
        storagePathPrefix={storagePathPrefix}
      />
    </FieldGroup>
  );
}

// FocusAreasGroup lets the instructor author optional "focus areas" for a
// thread. A student picks one before sending their next message; the Title is
// shown in their dropdown, while the instructions are hidden and only handed to
// Kai for that turn. Having none is fine — students then just message Kai.
function FocusAreasGroup({
  focusAreas,
  onChange,
}: {
  focusAreas: EditableFocusArea[];
  onChange: (next: EditableFocusArea[]) => void;
}) {
  return (
    <FieldGroup
      help="Optional. Things a student can choose to work on before their next message, such as “My thesis”. Kai gets your instructions for the area they pick."
      legend="Focus areas"
    >
      {focusAreas.length === 0 ? <p className={styles.empty}>No focus areas yet.</p> : null}
      {focusAreas.map((area) => (
        <div className={styles.note} key={area.uid}>
          <div className={styles.itemHeader}>
            <input
              aria-label="Focus area title"
              className={styles.input}
              onChange={(event) =>
                onChange(
                  focusAreas.map((a) =>
                    a.uid === area.uid ? { ...a, title: event.target.value } : a,
                  ),
                )
              }
              placeholder="Title shown to students (e.g. My thesis)"
              value={area.title}
            />
            <button
              aria-label={`Remove ${area.title.trim() || 'focus area'}`}
              className={styles.textButton}
              onClick={() => onChange(focusAreas.filter((a) => a.uid !== area.uid))}
              type="button"
            >
              Remove
            </button>
          </div>
          <label className={styles.fieldLabel}>
            What Kai should do for this focus (hidden from students)
            <textarea
              className={styles.textarea}
              onChange={(event) =>
                onChange(
                  focusAreas.map((a) =>
                    a.uid === area.uid ? { ...a, instructions: event.target.value } : a,
                  ),
                )
              }
              placeholder="e.g. Push the student to sharpen their thesis; don't rewrite it for them."
              rows={2}
              value={area.instructions}
            />
          </label>
        </div>
      ))}
      <div>
        <Button onClick={() => onChange([...focusAreas, newFocusArea()])} variant="secondary">
          Add focus area
        </Button>
      </div>
    </FieldGroup>
  );
}

function PointsInput({
  describedBy,
  onChange,
  placeholder,
  value,
}: {
  describedBy: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
}) {
  return (
    <input
      aria-describedby={describedBy}
      className={`${styles.input} ${styles.numberInput}`}
      min="0"
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      step="any"
      type="number"
      value={value}
    />
  );
}

// A scorable unit's credit settings, as one tinted group. Under participation:
// points and the message threshold. Under goal-reached credit: points, the goal
// Kai judges and the line students see — each saying whether it uses the
// coach's default or replaces it.
function CreditGroup({
  creditMethod,
  defaults,
  onChange,
  unit,
  unitName,
}: {
  creditMethod: EditableScoringMethod;
  // Set under goal-reached credit only.
  defaults: CreditDefaults | null;
  onChange: (next: Partial<EditableActivity>) => void;
  unit: EditableGoal & { points: string; requiredInteractions: string };
  // "session" or "activity", for the notes.
  unitName: string;
}) {
  if (creditMethod === 'PARTICIPATION' || !defaults) {
    return (
      <FieldGroup legend="Credit" tone="credit">
        <div className={styles.fieldRow}>
          <Field label="Points">
            {(describedBy) => (
              <PointsInput
                describedBy={describedBy}
                onChange={(points) => onChange({ points })}
                placeholder="e.g. 1"
                value={unit.points}
              />
            )}
          </Field>
          <Field
            help="Messages the student must send in this conversation to earn the points."
            label="Required messages"
          >
            {(describedBy) => (
              <input
                aria-describedby={describedBy}
                className={`${styles.input} ${styles.numberInput}`}
                min="1"
                onChange={(event) => onChange({ requiredInteractions: event.target.value })}
                placeholder="e.g. 2"
                step="1"
                type="number"
                value={unit.requiredInteractions}
              />
            )}
          </Field>
        </div>
      </FieldGroup>
    );
  }

  const ownPoints = unit.points.trim();
  const defaultPoints = defaults.points.trim();
  const defaultCriteria = defaults.goal.goalCriteria.trim();
  const defaultSummary = defaults.goal.goalSummary.trim();
  const ownCriteria = unit.goalCriteria.trim();
  const ownSummary = unit.goalSummary.trim();
  const explicitZero = ownPoints !== '' && Number(ownPoints) === 0;

  const pointsHelp =
    ownPoints === ''
      ? defaultPoints !== ''
        ? `Blank uses the default: ${pointsLabel(resolvedPoints('', defaults))}.`
        : `What this ${unitName} is worth. No default points are set.`
      : explicitZero
        ? `0 means this ${unitName} earns no credit.`
        : defaultPoints !== ''
          ? `Replaces the default of ${pointsLabel(resolvedPoints('', defaults))}.`
          : undefined;

  const goalHelp =
    ownCriteria === ''
      ? defaultCriteria !== ''
        ? `Blank uses the default goal: “${excerpt(defaultCriteria)}”`
        : 'What the student’s own work must show for Kai to award the points.'
      : defaultCriteria !== ''
        ? `Replaces the default goal for this ${unitName}.`
        : 'What the student’s own work must show for Kai to award the points.';

  const summaryHelp =
    ownSummary === ''
      ? defaultSummary !== ''
        ? ownCriteria !== ''
          ? `Blank uses the default: “${excerpt(defaultSummary)}” Check it fits this ${unitName}’s own goal.`
          : `Blank uses the default: “${excerpt(defaultSummary)}”`
        : 'Optional. A short line shown with the assignment.'
      : defaultSummary !== ''
        ? 'Replaces the default line.'
        : 'Shown to students with the assignment.';

  const points = resolvedPoints(unit.points, defaults);
  const hasGoal = resolvedHasGoal(unit, defaults);
  const note =
    points > 0 && !hasGoal
      ? `Kai can’t award this ${unitName}’s points until it has a goal. Write one here, or a default goal under Scoring.`
      : hasGoal && points <= 0 && !explicitZero
        ? 'Add points here, or default points under Scoring, so that reaching this goal earns credit.'
        : '';

  return (
    <FieldGroup legend="Credit" tone="credit">
      <Field help={pointsHelp} label="Points">
        {(describedBy) => (
          <PointsInput
            describedBy={describedBy}
            onChange={(next) => onChange({ points: next })}
            placeholder={defaultPoints !== '' ? `${defaultPoints} (default)` : 'e.g. 1'}
            value={unit.points}
          />
        )}
      </Field>
      <Field help={goalHelp} label="Goal — what Kai looks for (private to Kai)">
        {(describedBy) => (
          <textarea
            aria-describedby={describedBy}
            className={styles.textarea}
            onChange={(event) => onChange({ goalCriteria: event.target.value })}
            placeholder={
              defaultCriteria !== ''
                ? 'Leave blank to use the default goal'
                : 'e.g. The student states a clear claim and supports it with two pieces of evidence from the sources.'
            }
            rows={3}
            value={unit.goalCriteria}
          />
        )}
      </Field>
      <Field help={summaryHelp} label="Tell students (optional)">
        {(describedBy) => (
          <input
            aria-describedby={describedBy}
            className={styles.input}
            onChange={(event) => onChange({ goalSummary: event.target.value })}
            placeholder={
              defaultSummary !== ''
                ? 'Leave blank to use the default line'
                : 'e.g. State a thesis and the evidence for it'
            }
            value={unit.goalSummary}
          />
        )}
      </Field>
      {note ? (
        <p aria-live="polite" className={styles.liveWarning} role="status">
          {note}
        </p>
      ) : null}
    </FieldGroup>
  );
}

// --- scoring --------------------------------------------------------------------

function ScoringBlock({
  coach,
  setCoach,
}: {
  coach: EditableCoach;
  setCoach: (next: EditableCoach) => void;
}) {
  const goalMethod = coach.scoringMethod === 'GOAL_REACHED';
  return (
    <section className={styles.block}>
      <h2 className={styles.blockTitle}>Scoring</h2>
      <p className={styles.help}>
        By default a coach has no score. Turn on credit to give students points for
        their work with Kai. Scores appear in your gradebook like any assignment, and
        you can change any student’s score there.
      </p>
      <label className={styles.toggleRow}>
        <input
          checked={coach.creditEnabled}
          onChange={(event) => setCoach({ ...coach, creditEnabled: event.target.checked })}
          type="checkbox"
        />
        Enable credit
      </label>
      {coach.creditEnabled ? (
        <>
          <fieldset className={styles.methodGroup}>
            <legend className={styles.groupLegend}>How students earn credit</legend>
            <div className={styles.methodOptions}>
              <label className={styles.methodOption}>
                <input
                  checked={coach.scoringMethod === 'PARTICIPATION'}
                  name="kai-coach-credit-method"
                  onChange={() => setCoach({ ...coach, scoringMethod: 'PARTICIPATION' })}
                  type="radio"
                  value="PARTICIPATION"
                />
                <span>
                  <strong>Participation</strong> — a session’s points once the student has
                  sent the number of messages you set.
                </span>
              </label>
              <label className={styles.methodOption}>
                <input
                  checked={goalMethod}
                  name="kai-coach-credit-method"
                  onChange={() => setCoach({ ...coach, scoringMethod: 'GOAL_REACHED' })}
                  type="radio"
                  value="GOAL_REACHED"
                />
                <span>
                  <strong>Goal reached</strong> — Kai awards a session’s points when the
                  student’s work meets the goal you describe for it.
                </span>
              </label>
            </div>
          </fieldset>
          {goalMethod ? (
            <FieldGroup
              help={
                <>
                  Every session — or every activity, when a session is split — uses these
                  unless it sets its own below. Kai judges each of the student’s messages
                  against the goal, from the first one; students never see its judgement,
                  only the points when they earn them.
                </>
              }
              legend="Default credit"
              tone="credit"
            >
              <Field
                help="What each session is worth unless it sets its own points."
                label="Points"
              >
                {(describedBy) => (
                  <PointsInput
                    describedBy={describedBy}
                    onChange={(defaultPoints) => setCoach({ ...coach, defaultPoints })}
                    placeholder="e.g. 1"
                    value={coach.defaultPoints}
                  />
                )}
              </Field>
              <Field
                help="What the student’s own work must show. Write it so you could check it yourself."
                label="Goal — what Kai looks for (private to Kai)"
              >
                {(describedBy) => (
                  <textarea
                    aria-describedby={describedBy}
                    className={styles.textarea}
                    onChange={(event) =>
                      setCoach({
                        ...coach,
                        defaultGoal: { ...coach.defaultGoal, goalCriteria: event.target.value },
                      })
                    }
                    placeholder="e.g. The student states a clear claim and supports it with two pieces of evidence from the sources."
                    rows={3}
                    value={coach.defaultGoal.goalCriteria}
                  />
                )}
              </Field>
              <Field
                help="A short line students see with each assignment."
                label="Tell students (optional)"
              >
                {(describedBy) => (
                  <input
                    aria-describedby={describedBy}
                    className={styles.input}
                    onChange={(event) =>
                      setCoach({
                        ...coach,
                        defaultGoal: { ...coach.defaultGoal, goalSummary: event.target.value },
                      })
                    }
                    placeholder="e.g. State a thesis and the evidence for it"
                    value={coach.defaultGoal.goalSummary}
                  />
                )}
              </Field>
            </FieldGroup>
          ) : (
            <p className={styles.help}>
              Set <strong>points</strong> and <strong>required messages</strong> on each
              session below (or on each activity when a session is split). Students see
              their score out of the total of every open session.
            </p>
          )}
        </>
      ) : null}
    </section>
  );
}

// --- sessions ---------------------------------------------------------------------

function ActivityCard({
  activity,
  autoFocusName,
  creditMethod,
  defaults,
  index,
  onAutoFocused,
  onChange,
  onRemove,
  openAtMillis,
  storagePathPrefix,
}: {
  activity: EditableActivity;
  // True for an activity just added: its name field takes focus, once.
  autoFocusName: boolean;
  creditMethod: EditableScoringMethod | null;
  defaults: CreditDefaults | null;
  index: number;
  onAutoFocused: () => void;
  onChange: (next: EditableActivity) => void;
  onRemove: () => void;
  // The session's open date, which the activity's due date is checked against.
  openAtMillis: string;
  storagePathPrefix: string;
}) {
  const nameRef = useRef<HTMLInputElement>(null);
  const name = activity.label.trim() || `Activity ${index + 1}`;

  useEffect(() => {
    if (autoFocusName) {
      nameRef.current?.focus();
      onAutoFocused();
    }
  }, [autoFocusName, onAutoFocused]);

  return (
    <div className={styles.activityCard}>
      <div className={styles.itemHeader}>
        <h5 className={styles.activityTitle}>{name}</h5>
        <button
          aria-label={`Remove ${name}`}
          className={styles.textButton}
          onClick={onRemove}
          type="button"
        >
          Remove
        </button>
      </div>
      <Field label="Activity name">
        {() => (
          <input
            className={styles.input}
            onChange={(event) => onChange({ ...activity, label: event.target.value })}
            placeholder="e.g. Discussion"
            ref={nameRef}
            value={activity.label}
          />
        )}
      </Field>
      <Field help="Optional. Blank shows students the session’s prompt." label="Assignment prompt">
        {(describedBy) => (
          <textarea
            aria-describedby={describedBy}
            className={styles.textarea}
            onChange={(event) => onChange({ ...activity, prompt: event.target.value })}
            rows={2}
            value={activity.prompt}
          />
        )}
      </Field>
      <FieldGroup legend="Schedule">
        <DueDateField
          dueAtMillis={activity.dueAtMillis}
          help="Optional. Replaces the session’s due date for this activity."
          onChange={(dueAtMillis) => onChange({ ...activity, dueAtMillis })}
          openAtMillis={openAtMillis}
        />
      </FieldGroup>
      {creditMethod ? (
        <CreditGroup
          creditMethod={creditMethod}
          defaults={defaults}
          onChange={(partial) => onChange({ ...activity, ...partial })}
          unit={activity}
          unitName="activity"
        />
      ) : null}
      <CoachingNotesGroup
        notes={activity.notes}
        onChange={(notes) => onChange({ ...activity, notes })}
        storagePathPrefix={storagePathPrefix}
      />
      <FocusAreasGroup
        focusAreas={activity.focusAreas}
        onChange={(focusAreas) => onChange({ ...activity, focusAreas })}
      />
    </div>
  );
}

// What a collapsed session shows under its name, and what needs fixing.
function sessionSummary(
  session: EditableSession,
  creditMethod: EditableScoringMethod | null,
  defaults: CreditDefaults | null,
): { flags: string[]; parts: string[] } {
  const parts: string[] = [];
  const opens = summaryDate(session.openAtMillis);
  const due = summaryDate(session.dueAtMillis);
  if (opens) {
    parts.push(`Opens ${opens}`);
  }
  if (due) {
    parts.push(`Due ${due}`);
  }
  const activities = session.activities;
  if (activities.length > 0) {
    parts.push(`${activities.length} ${activities.length === 1 ? 'activity' : 'activities'}`);
  }
  // An activity with its own deadline, so every due date shows while collapsed.
  activities.forEach((activity, index) => {
    const activityDue = summaryDate(activity.dueAtMillis);
    if (activityDue) {
      parts.push(`${activity.label.trim() || `Activity ${index + 1}`} due ${activityDue}`);
    }
  });
  const units: Array<EditableGoal & { points: string }> =
    activities.length > 0 ? activities : [session];
  if (creditMethod) {
    const total = units.reduce((sum, unit) => sum + resolvedPoints(unit.points, defaults), 0);
    if (total > 0) {
      parts.push(pointsLabel(total));
    }
  }
  const flags: string[] = [];
  if (creditMethod && units.some((unit) => unitNeedsGoal(unit, defaults))) {
    flags.push('Needs a goal');
  }
  if (
    dueNotAfterOpen(session.openAtMillis, session.dueAtMillis) ||
    activities.some((activity) => dueNotAfterOpen(session.openAtMillis, activity.dueAtMillis))
  ) {
    flags.push('Check dates');
  }
  return { flags, parts };
}

function SessionCard({
  autoFocusName,
  chapters,
  creditMethod,
  defaults,
  expanded,
  index,
  initialContextUids,
  onAutoFocused,
  onChange,
  onMove,
  onRemove,
  onToggle,
  session,
  storagePathPrefix,
  total,
}: {
  // True for a session just added: its name field takes focus, once.
  autoFocusName: boolean;
  chapters: ChapterOption[];
  // null while credit is off.
  creditMethod: EditableScoringMethod | null;
  defaults: CreditDefaults | null;
  expanded: boolean;
  index: number;
  // Returns what was attached when the editor opened, so the picker can tell a
  // real change from an untouched selection.
  initialContextUids: (sessionUid: string) => string[] | null;
  onAutoFocused: () => void;
  onChange: (next: EditableSession) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
  onToggle: () => void;
  session: EditableSession;
  storagePathPrefix: string;
  total: number;
}) {
  const baseId = useId();
  const panelId = `${baseId}-panel`;
  const summaryId = `${baseId}-summary`;
  const nameRef = useRef<HTMLInputElement>(null);
  // The activity just added, whose name field takes focus.
  const [focusActivityUid, setFocusActivityUid] = useState<string | null>(null);
  const hasActivities = session.activities.length > 0;
  const name = session.label.trim() || `Session ${index + 1}`;
  const { flags, parts } = sessionSummary(session, creditMethod, defaults);

  useEffect(() => {
    if (autoFocusName && expanded) {
      nameRef.current?.focus();
      onAutoFocused();
    }
  }, [autoFocusName, expanded, onAutoFocused]);

  const clearActivityFocus = useCallback(() => setFocusActivityUid(null), []);

  function addActivity() {
    const activity = newActivity();
    onChange({ ...session, activities: [...session.activities, activity] });
    setFocusActivityUid(activity.uid);
  }

  function updateActivity(next: EditableActivity) {
    onChange({
      ...session,
      activities: session.activities.map((a) => (a.uid === next.uid ? next : a)),
    });
  }

  return (
    <div className={styles.sessionCard}>
      <div className={styles.sessionHeader}>
        <h3 className={styles.sessionHeading}>
          <button
            aria-controls={panelId}
            aria-describedby={parts.length > 0 || flags.length > 0 ? summaryId : undefined}
            aria-expanded={expanded}
            className={styles.disclosure}
            onClick={onToggle}
            type="button"
          >
            <span aria-hidden="true" className={styles.chevron} />
            <span className={styles.sessionName}>{name}</span>
          </button>
        </h3>
        <div className={styles.sessionActions}>
          <button
            aria-label={`Move ${name} up`}
            className={styles.iconButton}
            disabled={index === 0}
            onClick={() => onMove(-1)}
            type="button"
          >
            <span aria-hidden="true">↑</span>
          </button>
          <button
            aria-label={`Move ${name} down`}
            className={styles.iconButton}
            disabled={index === total - 1}
            onClick={() => onMove(1)}
            type="button"
          >
            <span aria-hidden="true">↓</span>
          </button>
          <button
            aria-label={`Remove ${name}`}
            className={styles.textButton}
            onClick={onRemove}
            type="button"
          >
            Remove
          </button>
        </div>
        {parts.length > 0 || flags.length > 0 ? (
          <p className={styles.sessionSummary} id={summaryId}>
            {parts.length > 0 ? <span>{parts.join(' · ')}</span> : null}
            {flags.map((flag) => (
              <span className={styles.flag} key={flag}>
                {flag}
              </span>
            ))}
          </p>
        ) : null}
      </div>

      <div className={styles.sessionBody} hidden={!expanded} id={panelId}>
        <Field label="Session name">
          {() => (
            <input
              className={styles.input}
              onChange={(event) => onChange({ ...session, label: event.target.value })}
              placeholder="e.g. Week 1"
              ref={nameRef}
              value={session.label}
            />
          )}
        </Field>

        <FieldGroup legend="Schedule">
          <div className={styles.fieldRow}>
            <Field
              help="Optional. Students can enter from this date, and its coaching notes reach Kai. Blank opens it now."
              label="Opens on"
            >
              {(describedBy) => (
                <input
                  aria-describedby={describedBy}
                  className={styles.input}
                  onChange={(event) =>
                    onChange({ ...session, openAtMillis: localInputToMillis(event.target.value) })
                  }
                  type="datetime-local"
                  value={millisToLocalInput(session.openAtMillis)}
                />
              )}
            </Field>
            <DueDateField
              dueAtMillis={session.dueAtMillis}
              help="Optional. After this, students can read the conversation but not send messages."
              onChange={(dueAtMillis) => onChange({ ...session, dueAtMillis })}
              openAtMillis={session.openAtMillis}
            />
          </div>
        </FieldGroup>

        <Field help="Shown to students." label="Assignment prompt">
          {(describedBy) => (
            <textarea
              aria-describedby={describedBy}
              className={styles.textarea}
              onChange={(event) => onChange({ ...session, prompt: event.target.value })}
              placeholder="What should students do this session?"
              rows={2}
              value={session.prompt}
            />
          )}
        </Field>

        {/* Course material lives on the session whether or not it is split: the
            chapters apply to every activity, and the backend sends them into a
            thread once rather than on every turn. */}
        <ChapterContextPicker
          chapters={chapters}
          description="Chapters Kai reads as background for this session and every activity in it."
          initialUids={initialContextUids(session.uid)}
          isLive={isSessionLive(session)}
          onChange={(contextComponentUids) => onChange({ ...session, contextComponentUids })}
          selectedUids={session.contextComponentUids}
        />

        {!hasActivities ? (
          <>
            {creditMethod ? (
              <CreditGroup
                creditMethod={creditMethod}
                defaults={defaults}
                onChange={(partial) => onChange({ ...session, ...partial })}
                unit={session}
                unitName="session"
              />
            ) : null}
            <CoachingNotesGroup
              notes={session.notes}
              onChange={(notes) => onChange({ ...session, notes })}
              storagePathPrefix={storagePathPrefix}
            />
            <FocusAreasGroup
              focusAreas={session.focusAreas}
              onChange={(focusAreas) => onChange({ ...session, focusAreas })}
            />
            <div className={styles.splitRow}>
              <Button onClick={addActivity} variant="secondary">
                Split into activities
              </Button>
              <p className={styles.help}>
                For more than one kind of work, like a discussion post and a peer
                response. Each activity is its own conversation.
              </p>
            </div>
          </>
        ) : (
          <div className={styles.activities}>
            <h4 className={styles.activitiesTitle}>Activities</h4>
            <p className={styles.help}>
              Each activity is its own focused conversation, with its own credit, notes
              and focus areas.
            </p>
            <ol className={styles.cardList}>
              {session.activities.map((activity, activityIndex) => (
                <li key={activity.uid}>
                  <ActivityCard
                    activity={activity}
                    autoFocusName={focusActivityUid === activity.uid}
                    creditMethod={creditMethod}
                    defaults={defaults}
                    index={activityIndex}
                    onAutoFocused={clearActivityFocus}
                    onChange={updateActivity}
                    onRemove={() =>
                      onChange({
                        ...session,
                        activities: session.activities.filter((a) => a.uid !== activity.uid),
                      })
                    }
                    openAtMillis={session.openAtMillis}
                    storagePathPrefix={storagePathPrefix}
                  />
                </li>
              ))}
            </ol>
            <div>
              <Button onClick={addActivity} variant="secondary">
                Add activity
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function KaiCoachEditor({ context, focusOnOpen = false, onExit, section }: KaiCoachEditorProps) {
  const [coach, setCoach] = useState<EditableCoach>(() =>
    toEditableCoach(section, { title: context.baseComponent.title }),
  );
  // The coach as last saved: as it opened, then as each successful save sent
  // it. Anything that differs is unsaved. Typing a value back makes it clean.
  const [baseline, setBaseline] = useState<EditableCoach>(coach);
  const isDirty = useMemo(
    () => coach !== baseline && JSON.stringify(coach) !== JSON.stringify(baseline),
    [baseline, coach],
  );
  const [showPreview, setShowPreview] = useState(false);
  // Set only by a refused save; cleared as soon as the title is non-blank.
  const [titleError, setTitleError] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const save = useSaveKaiCoach(context);

  // Discarding asks first, whether it comes from Cancel or from leaving the
  // page with unsaved changes (a navigation the guard holds).
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [heldNavigation, setHeldNavigation] = useState<HeldNavigation | null>(null);
  // Lets the editor's own exit through the guard after a discard, while the
  // last render still counted the changes.
  const allowLeaveRef = useRef(false);

  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focusOnOpen) {
      headingRef.current?.focus();
    }
  }, [focusOnOpen]);

  // A long coach opens as a list of sessions; a coach with one session opens
  // with it expanded.
  const [expandedUids, setExpandedUids] = useState<Set<string>>(
    () => new Set(coach.sessions.length === 1 ? [coach.sessions[0].uid] : []),
  );
  // The session just added, whose name field takes focus.
  const [focusSessionUid, setFocusSessionUid] = useState<string | null>(null);
  const clearSessionFocus = useCallback(() => setFocusSessionUid(null), []);

  const previewSection = useMemo(
    () => serializeCoachSection(coach, context.coachUid) as RawKaiCoachSection,
    [coach, context.coachUid],
  );

  const creditMethod = coach.creditEnabled ? coach.scoringMethod : null;
  const defaults = useMemo<CreditDefaults | null>(
    () =>
      creditMethod === 'GOAL_REACHED'
        ? { goal: coach.defaultGoal, points: coach.defaultPoints }
        : null,
    [coach.defaultGoal, coach.defaultPoints, creditMethod],
  );

  // Every chapter in the course, EXCLUDING this coach module itself. The course
  // query is already in the React Query cache (the content page loaded it), so
  // this is cache-served rather than a second fetch. An instructor's course data
  // is not visibility-filtered, so drafts and scheduled chapters are included on
  // purpose — attaching one only ever feeds the AI, never the student.
  const courseQuery = useCourse(context.courseId);
  const chapters = useMemo<ChapterOption[]>(
    () =>
      (courseQuery.data?.components ?? [])
        .filter((item) => item.kind === 'component')
        .map((item) => ({
          statusLabel: item.statusLabel,
          title: item.title,
          uid: item.componentUid || item.id,
        }))
        .filter((chapter) => chapter.uid !== '' && chapter.uid !== context.componentId),
    [courseQuery.data?.components, context.componentId],
  );

  // What was attached when the editor opened. Captured once so the picker can
  // distinguish a real edit from an untouched selection and only warn about a
  // change that actually happened this sitting.
  const initialCoachRef = useRef(coach);
  const initialContextUids = useCallback((sessionUid: string): string[] | null => {
    const session = initialCoachRef.current.sessions.find((s) => s.uid === sessionUid);
    // Newly added this sitting: no student can be mid-conversation in it.
    return session ? session.contextComponentUids : null;
  }, []);

  const storagePathPrefix = `kai-coach/${context.courseId}/${context.componentId}`;
  const allExpanded =
    coach.sessions.length > 0 && coach.sessions.every((s) => expandedUids.has(s.uid));

  function moveSession(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= coach.sessions.length) {
      return;
    }
    const next = [...coach.sessions];
    [next[index], next[target]] = [next[target], next[index]];
    setCoach({ ...coach, sessions: next });
  }

  function toggleSession(uid: string) {
    setExpandedUids((current) => {
      const next = new Set(current);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  }

  function addSession() {
    const session = newSession();
    setCoach({ ...coach, sessions: [...coach.sessions, session] });
    setExpandedUids((current) => new Set(current).add(session.uid));
    setFocusSessionUid(session.uid);
  }

  async function handleSave() {
    if (!coach.title.trim()) {
      // A blank title would be kept by the payload anyway, but a save that
      // silently ignores what the instructor typed is worse than a refusal.
      setTitleError(true);
      titleInputRef.current?.focus();
      return;
    }
    // Edits made while the save runs stay unsaved: the baseline becomes what
    // was sent, not what is on screen when it returns.
    const sent = coach;
    try {
      await save.mutateAsync(sent);
      setBaseline(sent);
    } catch {
      // Shown in the edit bar's status line (mapped, never raw).
    }
  }

  function discardChanges() {
    if (heldNavigation) {
      setHeldNavigation(null);
      heldNavigation.proceed();
      return;
    }
    setConfirmingCancel(false);
    setCoach(baseline);
    allowLeaveRef.current = true;
    onExit?.();
  }

  function keepEditing() {
    heldNavigation?.reset();
    setHeldNavigation(null);
    setConfirmingCancel(false);
  }

  return (
    <section className={styles.page}>
      <header className={styles.intro}>
        {/* The page's h1 is the module's title, above; this introduces the editor. */}
        <h2 className={styles.title} ref={headingRef} tabIndex={-1}>
          Kai Coach
        </h2>
        <p>A Kai Coach has three parts.</p>
        <ol className={styles.introList}>
          <li>
            <strong>Standing instructions</strong> — who Kai is and the rules it always
            follows: its personality, its method, and what it must never do. Kai uses these
            in every conversation.
          </li>
          <li>
            <strong>Sessions</strong> — the steps your students work through over the term.
            Each session has a date it opens, a prompt your students see, and your private
            notes telling Kai how to coach that assignment. Kai only gets a session’s notes
            once it opens.
          </li>
          <li>
            <strong>Activities</strong> (optional) — if one session has more than one kind
            of work, like a discussion post <em>and</em> a peer response, split it into
            activities. Each becomes its own focused conversation.
          </li>
        </ol>
      </header>

      <div className={styles.toolbar}>
        <Button onClick={() => setShowPreview((value) => !value)} variant="secondary">
          {showPreview ? 'Back to editing' : 'Preview as student'}
        </Button>
      </div>

      {showPreview ? (
        <KaiCoachStudentView
          courseId={context.courseId}
          moduleId={context.componentId}
          section={previewSection}
        />
      ) : (
        <div className={styles.editor}>
          <section className={styles.block}>
            <label className={styles.fieldLabel}>
              Coach title
              <input
                aria-describedby={titleError ? 'kai-coach-title-error' : undefined}
                aria-invalid={titleError || undefined}
                className={styles.input}
                onChange={(event) => {
                  if (titleError && event.target.value.trim()) {
                    setTitleError(false);
                  }
                  setCoach({ ...coach, title: event.target.value });
                }}
                ref={titleInputRef}
                required
                type="text"
                value={coach.title}
              />
            </label>
            {titleError ? (
              <p className={styles.error} id="kai-coach-title-error" role="alert">
                Enter a title for this coach.
              </p>
            ) : null}
            <p className={styles.help}>
              The module’s name: the heading of this page and its entry on the course
              home page.
            </p>
          </section>

          <section className={styles.block}>
            <h2 className={styles.blockTitle}>Student orientation</h2>
            <p className={styles.help} id="kai-coach-orientation-help">
              Shown to students above the chat. Public — not sent to Kai as instructions.
            </p>
            <textarea
              aria-describedby="kai-coach-orientation-help"
              aria-label="Student orientation"
              className={styles.textarea}
              onChange={(event) => setCoach({ ...coach, orientation: event.target.value })}
              rows={3}
              value={coach.orientation}
            />
          </section>

          <section className={styles.block}>
            <h2 className={styles.blockTitle}>Standing instructions</h2>
            <p className={styles.help}>
              Who Kai is and the rules it always follows. Used in every conversation in
              this coach.
            </p>
            <NoteList
              addLabel="Add instruction"
              emptyText="No standing instructions yet."
              noun="Instruction"
              notes={coach.standingInstructions}
              onChange={(standingInstructions) => setCoach({ ...coach, standingInstructions })}
              showVisibility
              storagePathPrefix={storagePathPrefix}
            />
          </section>

          <ScoringBlock coach={coach} setCoach={setCoach} />

          <section className={styles.block}>
            <div className={styles.blockHeader}>
              <h2 className={styles.blockTitle}>Sessions</h2>
              {coach.sessions.length > 1 ? (
                <button
                  className={styles.textButton}
                  onClick={() =>
                    setExpandedUids(
                      allExpanded ? new Set() : new Set(coach.sessions.map((s) => s.uid)),
                    )
                  }
                  type="button"
                >
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </button>
              ) : null}
            </div>
            <p className={styles.help}>
              The steps students work through over the term. Each opens on a date, shows
              students a prompt, and carries your private coaching notes for that step.
            </p>
            {coach.sessions.length === 0 ? (
              <p className={styles.empty}>Add your first session to get started.</p>
            ) : (
              <ol className={styles.cardList}>
                {coach.sessions.map((session, index) => (
                  <li key={session.uid}>
                    <SessionCard
                      autoFocusName={focusSessionUid === session.uid}
                      chapters={chapters}
                      creditMethod={creditMethod}
                      defaults={defaults}
                      expanded={expandedUids.has(session.uid)}
                      index={index}
                      initialContextUids={initialContextUids}
                      onAutoFocused={clearSessionFocus}
                      onChange={(next) =>
                        setCoach({
                          ...coach,
                          sessions: coach.sessions.map((s) => (s.uid === session.uid ? next : s)),
                        })
                      }
                      onMove={(direction) => moveSession(index, direction)}
                      onRemove={() =>
                        setCoach({
                          ...coach,
                          sessions: coach.sessions.filter((s) => s.uid !== session.uid),
                        })
                      }
                      onToggle={() => toggleSession(session.uid)}
                      session={session}
                      storagePathPrefix={storagePathPrefix}
                      total={coach.sessions.length}
                    />
                  </li>
                ))}
              </ol>
            )}
            <div>
              <Button onClick={addSession} variant="primary">
                Add session
              </Button>
            </div>
          </section>

          <details className={styles.block}>
            <summary className={styles.blockTitle}>AI settings</summary>
            <div className={styles.aiSettings}>
              <label className={styles.fieldLabel}>
                Provider
                <select
                  className={styles.select}
                  onChange={(event) => setCoach({ ...coach, provider: event.target.value })}
                  value={coach.provider}
                >
                  <option value="OPENAI">OpenAI</option>
                  <option value="ANTHROPIC">Anthropic (coming soon)</option>
                  <option value="GOOGLE">Google (coming soon)</option>
                </select>
              </label>
            </div>
          </details>
        </div>
      )}

      <CoachEditBar
        isDirty={isDirty}
        isSaving={save.isPending}
        onCancel={() => setConfirmingCancel(true)}
        onDone={() => onExit?.()}
        onSave={() => void handleSave()}
        saveFailed={save.isError}
      />
      <DiscardCoachChangesDialog
        onDiscard={discardChanges}
        onKeepEditing={keepEditing}
        open={confirmingCancel || heldNavigation !== null}
      />
      <UnsavedChangesGuard
        allowRef={allowLeaveRef}
        onBlocked={setHeldNavigation}
        when={isDirty}
      />
    </section>
  );
}
